import { useEffect, useState } from "react";
import { Alert, Button, Container, Spinner } from "react-bootstrap";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { BiAnalyse } from "react-icons/bi";
import { BsCardList } from "react-icons/bs";
import { IoMdClose } from "react-icons/io";
import { useSupabaseApp } from "../../SupabaseApp";
import PartnersPerkCard from "../cards/partners-perk/card";
import ApplicationModal from "./application-modal";
import Styles from "./page.module.css";
import PerkDetailModal from "./perk-modal";
import PaywallModal from "../modals/paywall-modal";
import { availablePerks } from "./available-perks";
import { getFunctionErrorMessage } from "../../utils/network-errors";

/**
 * Partner Marketplace - Perks Discovery & Redemption
 *
 * Browse partner perks by category or search, view detailed perk information, and apply
 * for perks through a dynamic application form. Tier-gated access ensures only users with
 * appropriate subscription levels can redeem certain perks. Paywall modal presented for
 * restricted perks.
 *
 * Features:
 * - Perk discovery with search and filtering
 * - Detailed perk information modal
 * - Dynamic application form based on discovery source (Email, Social media, etc.)
 * - Tier-based access control (Freemium, Standard, Premium)
 * - Form validation and Supabase submission
 * - Real-time alert notifications
 * - Paywall for premium-tier-only perks
 *
 * @returns {JSX.Element} Complete partner perks marketplace UI
 */

/**
 * Hear About Us Options Configuration
 *
 * Maps different discovery sources to dynamic form fields. When user selects where
 * they heard about the perk, corresponding form fields appear for context collection.
 */
export const hearAboutUsOptions = {
  Email: {
    fields: [
      // {
      //   label: 'Which email or newsletter campaign?',
      //   name: 'emailCampaign',
      //   type: 'text'
      // },
      {
        label: "Describe the topic and content of the email or newsletter*",
        name: "emailDescription",
        type: "textarea",
        required: true,
      },
      {
        label: "When did you receive the email or newsletter?",
        name: "emailDate",
        type: "date",
      },
      {
        label: "Copy the URL/link and paste it below (if any)",
        name: "emailUrl",
        type: "text",
      },
    ],
  },
  "Social media": {
    fields: [
      // {
      //   label: 'Which social media post or content?',
      //   name: 'socialMediaContent',
      //   type: 'text'
      // },
      {
        label: "Which social media platform did you see the post or content?*",
        name: "socialMediaPlatform",
        type: "text",
        required: true,
      },
      {
        label:
          "Describe the topic and content of the social media post or content*",
        name: "socialMediaDescription",
        type: "textarea",
        required: true,
      },
      {
        label: "Was it a promoted (paid) or organic (non-paid) content?*",
        name: "socialMediaPaid",
        type: "select",
        options: ["Promoted content (paid)", "Organic content (non-paid)"],
        required: true,
      },
      {
        label: "When did you see the post or content?",
        name: "socialMediaDate",
        type: "date",
      },
      {
        label: "Copy the URL/link and paste it below (if any)",
        name: "socialMediaURL",
        type: "text",
      },
    ],
  },
  "Search engine": {
    fields: [
      // {
      //   label: 'What content appeared as one of the search results?',
      //   name: 'searchContent',
      //   type: 'text'
      // },
      {
        label: "Which search engine platform did you see the content?*",
        name: "searchEnginePlatform",
        type: "select",
        options: [
          "Google",
          "Microsoft Bing",
          "Yahoo!",
          "DuckDuckGo",
          "Brave Search",
          "AOL Search",
          "Qwant",
          "Other",
        ],
        required: true,
      },
      {
        label: "Was it a promoted (paid) or organic (non-paid) content?*",
        name: "searchPaid",
        type: "select",
        options: [
          "Promoted content (such as Google ad)",
          "Organic content (such as search results)",
        ],
        required: true,
      },
      {
        label: "Describe the topic and content of what you saw*",
        name: "searchDescription",
        type: "textarea",
        required: true,
      },
      {
        label: "When did you see the content?",
        name: "searchDate",
        type: "date",
      },
      {
        label: "Copy the URL/link and paste it below (if any)",
        name: "searchUrl",
        type: "text",
      },
    ],
  },
  "Media advertisement": {
    fields: [
      // {
      //   label: 'Which advertisement campaign?',
      //   name: 'mediaCampaign',
      //   type: 'text'
      // },
      {
        label:
          "Which media platform did you see the advertisement or sponsored content?*",
        name: "adPlatform",
        type: "text",
        required: true,
      },
      {
        label:
          "Describe the topic and content of the advertisement or sponsored content*",
        name: "adDescription",
        type: "textarea",
        required: true,
      },
      {
        label: "When did you see the advertisement or sponsored content?",
        name: "adDate",
        type: "date",
      },
      {
        label: "Copy the URL/link and paste it below (if any)",
        name: "adUrl",
        type: "text",
      },
    ],
  },
  "Word of mouth": {
    fields: [
      // { label: 'What did you hear about us?', name: 'wordHeard', type: 'text' },
      {
        label: "Describe the topic and content of what you heard*",
        name: "wordOfMouthDescription",
        type: "textarea",
        required: true,
      },
      {
        label: "When did you have the conversation?",
        name: "wordOfMouthDate",
        type: "date",
      },
      {
        label: "Who did you hear it from (if any)",
        name: "wordOfMouthFrom",
        type: "text",
      },
    ],
  },
  "Government authority or agency": {
    fields: [
      // {
      //   label: 'What information was shared with you?',
      //   name: 'govInfo',
      //   type: 'text'
      // },
      {
        label: "Describe the topic and content of the information*",
        name: "govDescription",
        type: "textarea",
        required: true,
      },
      {
        label: "How did you receive the information?*",
        name: "govReceivedMethod",
        type: "text",
        required: true,
      },
      {
        label: "When did you receive the information?",
        name: "govDate",
        type: "date",
      },
      {
        label: "Copy the URL/link and paste it below (if any)",
        name: "govUrl",
        type: "text",
      },
    ],
  },
  "Recommended by friend or associate or affiliate": {
    fields: [
      {
        label: "Who recommended us to you?*",
        name: "recommendWhoName",
        type: "text",
        required: true,
      },
      {
        label: "How did you receive the recommendation?*",
        name: "recommendHow",
        type: "text",
        required: true,
      },
      {
        label: "When did you receive the recommendation?",
        name: "recommendDate",
        type: "date",
      },
      {
        label:
          "Share the email or social media of the friend or associate or affiliate who recommended us to you (if any)",
        name: "recommendContact",
        type: "text",
      },
    ],
  },
  "Research paper or analytical report": {
    fields: [
      // {
      //   label: 'Which research paper or analytical report?',
      //   name: 'reportTitle',
      //   type: 'text'
      // },
      {
        label:
          "Which platform did you see the research paper or analytical report?*",
        name: "researchPlatform",
        type: "text",
        required: true,
      },
      {
        label:
          "Describe the topic and content of the research paper or analytical report*",
        name: "researchDescription",
        type: "textarea",
        required: true,
      },
      {
        label: "When did you see the research paper or analytical report?",
        name: "researchDate",
        type: "date",
      },
      {
        label: "Copy the URL/link and paste it below (if any)",
        name: "researchUrl",
        type: "text",
      },
    ],
  },
  "News article or blog or website": {
    fields: [
      // {
      //   label: 'Which news article or blog or website?',
      //   name: 'newsTitle',
      //   type: 'text'
      // },
      {
        label:
          "Describe the topic and content of the news article or blog or website*",
        name: "newsDescription",
        type: "textarea",
        required: true,
      },
      {
        label: "When did you see the news article or blog or website?",
        name: "newsDate",
        type: "date",
      },
      {
        label: "Copy the URL/link and paste it below (if any)",
        name: "newsUrl",
        type: "text",
      },
    ],
  },
  "Social or networking event": {
    fields: [
      // {
      //   label: 'Which social or networking event?',
      //   name: 'eventTitle',
      //   type: 'text'
      // },
      {
        label: "Where was the social or networking event held at?*",
        name: "eventLocation",
        type: "text",
        required: true,
      },
      {
        label: "Describe the name and type of the social or networking event*",
        name: "eventDescription",
        type: "textarea",
        required: true,
      },
      {
        label: "When did you attend the social or networking event?",
        name: "eventDate",
        type: "date",
      },
      {
        label: "Copy the URL/link and paste it below (if any)",
        name: "eventUrl",
        type: "text",
      },
    ],
  },
  "School or university": {
    fields: [
      // {
      //   label: 'What information was shared with you?',
      //   name: 'schoolInfo',
      //   type: 'text'
      // },
      {
        label:
          "From which school or university did you receive the information?*",
        name: "schoolName",
        type: "text",
        required: true,
      },
      {
        label: "Describe the topic and content of the information*",
        name: "schoolDescription",
        type: "textarea",
        required: true,
      },
      {
        label: "When did you receive the information?",
        name: "schoolDate",
        type: "date",
      },
      {
        label: "Copy the URL/link and paste it below (if any)",
        name: "schoolUrl",
        type: "text",
      },
    ],
  },
  Podcast: {
    fields: [
      // { label: 'Which podcast content?', name: 'podcastTitle', type: 'text' },
      {
        label: "Which platform did you listen to the podcast content?*",
        name: "podcastPlatform",
        type: "text",
        required: true,
      },
      {
        label: "Describe the topic and content of the podcast content*",
        name: "podcastDescription",
        type: "textarea",
        required: true,
      },
      {
        label: "When did you listen to the podcast content?",
        name: "podcastDate",
        type: "date",
      },
      {
        label: "Copy the URL/link and paste it below (if any)",
        name: "podcastUrl",
        type: "text",
      },
    ],
  },
  Other: {
    fields: [
      {
        label: "Any useful information you may want to share with us?*",
        name: "otherInfo",
        type: "textarea",
        required: true,
      },
      {
        label: "Copy the URL/link and paste it below (if any)",
        name: "otherUrl",
        type: "text",
      },
    ],
  },
};

/**
 * Initial Form Values
 *
 * Default state object for perk application form. Contains all user profile fields,
 * perk selection fields, and dynamic "hearAboutUs" option field groups
 * for different discovery sources.
 */
const initialValues = {
  email: "",
  firstName: "",
  lastName: "",
  country: "",
  countryCode: "",
  state: "",
  otherState: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  zipCode: "",
  knowYouBetter: "",
  hearAboutUs: "",
  recommended: "",
  other: "",
  others: "",
  id: "", // name of the perk
  listId: null, // partners perk listId

  job: "",
  company: "",
  LinkedIn: "",
  contactNumber: "",

  // Dynamic fields for 'hearAboutUs' options
  emailCampaign: "",
  emailDescription: "",
  emailDate: "",
  emailUrl: "",

  socialMediaContent: "",
  socialMediaPlatform: "",
  socialMediaDescription: "",
  socialMediaPaid: "",
  socialMediaDate: "",
  socialMediaUrl: "",

  searchContent: "",
  searchEnginePlatform: "",
  searchPaid: "",
  searchDescription: "",
  searchDate: "",
  searchUrl: "",

  mediaCampaign: "",
  adPlatform: "",
  adDescription: "",
  adDate: "",
  adUrl: "",

  wordHeard: "",
  wordOfMouthDescription: "",
  wordOfMouthDate: "",
  wordOfMouthFrom: "",

  govInfo: "",
  govDescription: "",
  govReceivedMethod: "",
  govDate: "",
  govUrl: "",

  recommendWhoName: "",
  recommendHow: "",
  recommendDate: "",
  recommendContact: "",

  reportTitle: "",
  researchPlatform: "",
  researchDescription: "",
  researchDate: "",
  researchUrl: "",

  newsTitle: "",
  newsDescription: "",
  newsDate: "",
  newsUrl: "",

  eventTitle: "",
  eventLocation: "",
  eventDescription: "",
  eventDate: "",
  eventUrl: "",

  schoolInfo: "",
  schoolName: "",
  schoolDescription: "",
  schoolDate: "",
  schoolUrl: "",

  podcastTitle: "",
  podcastPlatform: "",
  podcastDescription: "",
  podcastDate: "",
  podcastUrl: "",

  otherInfo: "",
  otherUrl: "",
};

export default function PartnerMarketplace() {
  // Search state for filtering perks (currently not in active use)
  const [search, setSearch] = useState("");

  // Perk state management
  const [perks, setPerks] = useState(availablePerks); // All available perks
  const [selectedPerk, setSelectedPerk] = useState(null); // Current selected perk

  // Modal visibility states
  const [showDetailsModal, setShowDetailsModal] = useState(false); // Perk details modal
  const [showApplicationModal, setShowApplicationModal] = useState(false); // Application form modal
  const [showPaywallModal, setShowPaywallModal] = useState(false); // Paywall modal (for restricted perks)

  // Paywall configuration
  const [paywallType, setPaywallType] = useState("standard"); // Type of paywall to display
  const [loading, setLoading] = useState(false); // Form submission loading state

  // Alert/notification states
  const [alert, setAlert] = useState(false); // Show alert messages
  const [message, setMessage] = useState(
    <div style={{ display: "flex", paddingTop: "10px" }}>
      <div style={{ width: "90%" }}>
        <p style={{ color: "#0f9d58" }}>
          <BiAnalyse className="alert-icon" /> Loading
        </p>
        <p style={{ marginLeft: "28px", fontSize: "14px" }}>
          Won't be long now. We're always doing our best to reduce waiting time.
        </p>
        <p style={{ marginLeft: "28px", fontSize: "14px" }}>
          If your waiting time is too long,{" "}
          <a
            href="https://www.roiquant.com/contact"
            target="_blank"
            rel="noopener noreferrer"
          >
            contact us
          </a>
          .
        </p>
      </div>
      <div style={{ width: "10%", textAlign: "right" }}>
        <Spinner animation="border" variant="secondary" />
      </div>
    </div>,
  );

  // Application form state - all user input and selected perk info
  const [subscription, setSubscription] = useState(initialValues);

  // useEffect(() => {
  //   console.log('subscription:', subscription)
  // }, [subscription]);

  // Update form input field values
  function handleChange(e) {
    e.preventDefault();

    let name = e.target.name;
    let value = e.target.value;

    // create a copy of filters with desired changes
    let subscriptionCopy = { ...subscription };

    subscriptionCopy[name] = value;
    setSubscription({ ...subscriptionCopy });
  }

  // Handle hearing source change - reset dynamic fields and update selection
  const handleHearAboutUsChange = (e) => {
    const selectedOption = e.target.value;

    // Clear all dynamic fields first
    const resetFields = {};
    Object.values(hearAboutUsOptions).forEach((option) => {
      option.fields.forEach((field) => {
        resetFields[field.name] = "";
      });
    });

    // Update the selected option and reset others
    setSubscription((prev) => ({
      ...prev,
      ...resetFields,
      hearAboutUs: selectedOption,
    }));
  };

  // Close alert and clear message
  function closeAlert() {
    setAlert(false);
    setMessage("");
  }

  // Scroll viewport to results section
  function scrollToTop() {
    window.scrollTo(0, 0);

    document.getElementById("table-data").scrollIntoView();
  }

  // Get Supabase authentication and tier functions
  const { currentUser, logInAnonymous, getUserTier, supabase } =
    useSupabaseApp();

  // User's current subscription tier
  const [tier, setTier] = useState(null);

  // Fetch user tier on mount
  useEffect(() => {
    async function fetchTier() {
      const userTier = await getUserTier();
      setTier(userTier);
    }
    fetchTier();
  }, []);

  // Require authentication to access
  if (!currentUser) {
    throw new Error(`Must be logged in`);
  }

  // Submit perk application to Supabase
  async function handleSubmit(e) {
    // goal: send the collected information to save copies on both brevo (for emailing) and mongodb (for safekeeping)
    e.preventDefault();
    setShowApplicationModal(true);
    setAlert(true);

    try {
      setLoading(true);

      // console.log('subscription:', subscription)
      const { error } = await supabase.functions.invoke(
        "redemptionApplicationForm",
        {
          body: subscription,
        },
      );

      if (error) {
        const ctx = await error.context?.json?.().catch(() => ({}));
        const msg = ctx?.error ?? "unknown";
        throw new Error(msg);
      }

      // example json format
      // subscription :
      // {
      //   "perk": "intuit",
      //   "email": "tester@test.com",
      //   "firstName": "john",
      //   "lastName": "doe",
      //   "knowYouBetter": "know you better text",
      //   "hearAboutUs": "hear about us text"
      // }

      // reset form
      setSubscription(initialValues);
      setLoading(false);
      setShowApplicationModal(false); // close application modal

      setMessage(
        <div style={{ paddingTop: "10px" }}>
          <p style={{ color: "#1f5f8b" }}>
            <BsCardList className="alert-icon" /> Application Submitted
          </p>
          <p style={{ marginLeft: "28px" }}>
            Your application has been submitted.
          </p>
        </div>,
      );
    } catch (error) {
      // setLoading(false)
      const functionErrorUI = getFunctionErrorMessage(error);

      if (functionErrorUI) {
        setMessage(functionErrorUI);
        return;
      } else if (error?.message?.toLowerCase() === "upgrade plan") {
        setMessage(
          <div style={{ paddingTop: "10px" }}>
            <p style={{ color: "red" }}>
              <AiOutlineCloseCircle className="alert-icon" /> Error
            </p>
            <p style={{ marginLeft: "28px" }}>
              It seems that your current plan is not eligible for this partner's
              perks.
            </p>
            <p style={{ marginLeft: "28px" }}>
              This partner's perks are only available for annual plans
              customers.
            </p>
            <p style={{ marginLeft: "28px" }}>
              Please{" "}
              <a href="https://www.roiquant.com/pricing">upgrade your plan</a>{" "}
              accordingly and try again.
            </p>
            <p style={{ marginLeft: "28px" }}>
              If you still have trouble with your perks redemption, please{" "}
              <a href="https://www.roiquant.com/contact">
                contact our support team
              </a>{" "}
              for help.
            </p>
          </div>,
        );
      } else if (error?.message?.toLowerCase() === "only tier 2 can apply") {
        setMessage(
          <div style={{ paddingTop: "10px" }}>
            <p style={{ color: "red" }}>
              <AiOutlineCloseCircle className="alert-icon" /> Error
            </p>
            <p style={{ marginLeft: "28px" }}>
              It seems that your current plan is not eligible for this partner's
              perks.
            </p>
            <p style={{ marginLeft: "28px" }}>
              If you still have trouble with your perks redemption, please{" "}
              <a href="https://www.roiquant.com/contact">
                contact our support team
              </a>{" "}
              for help.
            </p>
          </div>,
        );
      } else {
        // TO-DO: Handle error
        setMessage(
          <div style={{ paddingTop: "10px" }}>
            <p style={{ color: "red" }}>
              <AiOutlineCloseCircle className="alert-icon" /> Error
            </p>
            <p style={{ marginLeft: "28px" }}>
              There seems to be an issue submitting the application. Please try
              again.
            </p>
            <p style={{ marginLeft: "28px" }}>
              If you still have trouble with submitting the application, please{" "}
              <a href="https://www.roiquant.com/contact">
                contact our support team
              </a>{" "}
              for help.
            </p>
          </div>,
        );
      }
    }
  }

  // Filter perks by search term (name or category match)
  const filteredPerks = perks.filter(
    (perk) =>
      perk.name.toLowerCase().includes(search.toLowerCase()) ||
      perk.category.toLowerCase().includes(search.toLowerCase()),
  );

  // Open perk detail modal
  const handleOpenModal = (perk) => {
    // Update the subscription state with the selected perk's listId
    setSubscription((prev) => ({
      ...prev,
      listId: perk.listId,
      id: perk.id.toLowerCase(),
    }));

    setSelectedPerk(perk);
    setShowDetailsModal(true);
  };

  // Map tier numbers to tier names (1=freemium, 2=standard, 3=premium)
  const tierMap = {
    1: "freemium",
    2: "standard",
    3: "premium",
  };

  // Map tier names to numbers for comparison
  const tierNameToNumber = {
    freemium: 1,
    standard: 2,
    premium: 3,
  };

  // Check if user's tier has access to perk
  const isPerkAccessible = (perk, userTierNumber) => {
    const userTierName = tierMap[userTierNumber];
    return perk?.bundleTiers?.includes(userTierName);
  };

  // Check if user's tier is eligible for perk
  const isTierEligible = (perk, userTierNumber) => {
    const userTierName = tierMap[userTierNumber];
    return perk?.bundleTiers?.includes(userTierName);
  };

  // Get minimum tier number required for perk
  const getMinimumRequiredTierNumber = (perk) => {
    if (!perk?.bundleTiers?.length) return 1;
    return Math.min(...perk.bundleTiers.map((tier) => tierNameToNumber[tier]));
  };

  // Handle redeem now action - show paywall if restricted
  const handleRedeemNow = () => {
    const userTierNumber = tier;
    const isEligible = isTierEligible(selectedPerk, userTierNumber);
    const requiredTierNumber = getMinimumRequiredTierNumber(selectedPerk);

    if (!isEligible) {
      setPaywallType(
        requiredTierNumber === 3
          ? "premium"
          : requiredTierNumber === 2
            ? "standard"
            : "standard",
      );
      setShowPaywallModal(true);
    } else {
      setShowApplicationModal(true);
    }

    setShowDetailsModal(false);
  };

  return (
    <>
      {alert ? (
        <Alert className="error-message floating-alert" variant="light">
          <div>
            <div style={{ textAlign: "right" }}>
              <Button className="pitchdeckmessageclose" onClick={closeAlert}>
                <IoMdClose />
              </Button>
            </div>
            {message}
          </div>
        </Alert>
      ) : (
        ""
      )}

      <Container
        fluid
        style={{
          paddingBottom: "50px",
          paddingTop: "0px",
          paddingLeft: "0px",
          paddingRight: "0px",
        }}
      >
        <div id={Styles.index}>
          <div className={Styles.indexcontent}>
            <div className="shutdowndataheaderdiv">
              <div className="shutdowndatatitlediv">
                <span className="shutdowndatatext">
                  Partners' perks
                  <sup
                    data-tip="Special deals and perks exclusively for roiquant customers."
                    currentitem="false"
                  >
                    <svg
                      stroke="currentColor"
                      fill="currentColor"
                      strokeWidth="0"
                      viewBox="0 0 512 512"
                      id="information-icon"
                      height="1em"
                      width="1em"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M256 90c44.3 0 86 17.3 117.4 48.6C404.7 170 422 211.7 422 256s-17.3 86-48.6 117.4C342 404.7 300.3 422 256 422s-86-17.3-117.4-48.6C107.3 342 90 300.3 90 256s17.3-86 48.6-117.4C170 107.3 211.7 90 256 90m0-42C141.1 48 48 141.1 48 256s93.1 208 208 208 208-93.1 208-208S370.9 48 256 48z"></path>
                      <path d="M277 360h-42V235h42v125zm0-166h-42v-42h42v42z"></path>
                    </svg>
                  </sup>
                  <p
                    style={{
                      fontStyle: "italic",
                      marginTop: "0.25rem",
                      fontSize: "12px",
                      fontWeight: 400,
                    }}
                  >
                    Disclaimer: The approval of perks redemption is not
                    guaranteed and is determined at the sole discretion of
                    roiquant's partners.
                  </p>
                </span>
              </div>
            </div>

            {/* <Col>
              <Row className='mb-4 g-3'>
                  <Col md={6} className='text-md-start mt-3'>
                    <Button variant='outline-secondary' style={{ fontSize: '.875em' }}>
                      <i className='bi bi-funnel'></i> Filter
                    </Button>
                  </Col>
                  <Col md={6}>
                    <Form.Control
                      type='text'
                      placeholder='Search perks or categories'
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                  </Col>
                </Row>
            </Col> */}
            <div
              className={
                Styles.indexcompanycard +
                " " +
                Styles.indexrow +
                " " +
                Styles.indexml1 +
                " " +
                Styles.indexmr0
              }
            >
              {filteredPerks.map((perk, index) => (
                <PartnersPerkCard
                  perk={perk}
                  handleOpenModal={handleOpenModal}
                  key={index}
                />
              ))}
            </div>
          </div>

          {/* the first pop up modal --> shows full description and redeem button */}
          <PerkDetailModal
            showDetailsModal={showDetailsModal}
            setShowDetailsModal={setShowDetailsModal}
            selectedPerk={selectedPerk}
            setShowApplicationModal={setShowApplicationModal}
            tier={tier}
            isPerkAccessible={isPerkAccessible}
            handleRedeemNow={handleRedeemNow}
          />

          {/* Modal with the survey */}
          <ApplicationModal
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            subscription={subscription}
            showApplicationModal={showApplicationModal}
            setShowApplicationModal={setShowApplicationModal}
            selectedPerk={selectedPerk}
            setSubscription={setSubscription}
            handleHearAboutUsChange={handleHearAboutUsChange}
            loading={loading}
          />

          <PaywallModal
            show={showPaywallModal}
            onHide={() => setShowPaywallModal(false)}
            selectedPerk={selectedPerk}
            paywallType={paywallType}
            userTierName={tierMap[tier]}
          />
        </div>
      </Container>
    </>
  );
}
